const DEFAULT_RETURN_TO = '/';
const PARSE_BASE = 'https://return-to.invalid';

/** Appended to the callback URL by Neon Auth after Google; it must never be carried into the next one. */
const SESSION_VERIFIER_PARAM = 'neon_auth_session_verifier';

/**
 * The open-redirect guard for the page sign-in sends the user back to: it hands
 * back its input only when that is a same-origin path, and `/` for anything
 * else. The input must already be in the URL parser's canonical form, so a
 * backslash, a dot segment or a stray control character, any of which a browser
 * may read differently from this check, is refused rather than repaired.
 */
export function safeReturnTo(value: string | null | undefined): string {
  if (!value?.startsWith('/')) {
    return DEFAULT_RETURN_TO;
  }

  let url: URL;
  try {
    url = new URL(value, PARSE_BASE);
  } catch {
    return DEFAULT_RETURN_TO;
  }

  const path = `${url.pathname}${url.search}${url.hash}`;
  return url.origin === PARSE_BASE && path === value ? value : DEFAULT_RETURN_TO;
}

/**
 * The path and query of the page the visitor is on, safe to come back to after
 * sign-in. The hash is dropped: Neon Auth adds its session verifier to the
 * callback URL's query, and a fragment before it is not worth risking that.
 */
export function returnToFor(location: Pick<Location, 'pathname' | 'search'>): string {
  const params = new URLSearchParams(location.search);
  let search = location.search;
  if (params.has(SESSION_VERIFIER_PARAM)) {
    params.delete(SESSION_VERIFIER_PARAM);
    const rest = params.toString();
    search = rest === '' ? '' : `?${rest}`;
  }
  return safeReturnTo(`${location.pathname}${search}`);
}
